import { Printer } from "lucide-react";
import React, { useContext, useEffect, useMemo, useState } from "react";
import Breadcrumbs from "~/components/Breadcrumbs";
import FiltersBar from "~/components/FiltersBar";
import PageHeader from "~/components/PageHeader";
import { getCurrentPage } from "~/functions";
import { useSelector } from "react-redux";
import { PageContext } from "~/App";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import api from "~/services/api";
import { toast } from "react-toastify";
import {
  getFilenameFromContentDisposition,
  openBlobInNewTab,
} from "~/utils/blobPreview";
import RateChart from "./RateChart";

export function SurveysRoot() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { pages } = useContext(PageContext);
  const paths = pathname.split("/");
  const redirectRouteName = pages
    .find((page) => page.name === paths[1])
    .children.find((page) => page.path === `/${paths[1]}/${paths[2]}`)
    .children[0].path;

  useEffect(() => {
    if (pathname.toUpperCase() === `/${paths[1]}/${paths[2]}`.toUpperCase()) {
      navigate(`${redirectRouteName}`);
    }
  }, [pathname]);

  return <Outlet />;
}

function getCurrentMonthRange() {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth();
  const from = new Date(year, month, 1);
  const to = new Date(year, month + 1, 0);
  const pad = (value) => String(value).padStart(2, "0");
  const toISO = (date) =>
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
  return { from: toISO(from), to: toISO(to) };
}

export default function Surveys() {
  const defaultPeriod = getCurrentMonthRange();
  const [surveys, setSurveys] = useState([]);
  const [periodFrom, setPeriodFrom] = useState(defaultPeriod.from);
  const [periodTo, setPeriodTo] = useState(defaultPeriod.to);
  const [selectedGroup, setSelectedGroup] = useState("all");
  const [selectedTeacher, setSelectedTeacher] = useState("all");
  const [selectedShift, setSelectedShift] = useState("all");
  const [selectedLevel, setSelectedLevel] = useState("all");
  const [groups, setGroups] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [availableShifts, setAvailableShifts] = useState([]);
  const [availableLevels, setAvailableLevels] = useState([]);
  const [loadingFilters, setLoadingFilters] = useState(false);
  const [viewMode, setViewMode] = useState("charts");
  const [textAnswers, setTextAnswers] = useState([]);
  const [expandedQuestionId, setExpandedQuestionId] = useState(null);
  const [generatingPdf, setGeneratingPdf] = useState(false);

  // verify state
  const [verifyLoading, setVerifyLoading] = useState(true);
  const [verifyError, setVerifyError] = useState(false);
  const [isFaculty, setIsFaculty] = useState(false);
  const [facultyStaffId, setFacultyStaffId] = useState(null);
  const [facultyStaffName, setFacultyStaffName] = useState(null);
  const [facultyGroupIds, setFacultyGroupIds] = useState([]);
  const [facultyGroups, setFacultyGroups] = useState([]);

  const filtersDisabled = !periodFrom || !periodTo;

  useEffect(() => {
    async function verifyFaculty() {
      try {
        const { data } = await api.get("/surveys/verify");
        if (data?.is_faculty === "yes") {
          setIsFaculty(true);
          const staffId = String(data.staff_id);
          const staffName = data.staff_name || "";
          const groupIds = (data?.group_ids || []).map(String);
          const groups = (data?.groups || []).map((g) => ({
            id: String(g.id),
            name: g.name,
          }));

          setFacultyStaffId(staffId);
          setFacultyStaffName(staffName);
          setFacultyGroupIds(groupIds);
          setFacultyGroups(groups);
          setSelectedTeacher(staffId);
          setTeachers([{ id: staffId, name: staffName }]);
          setGroups(groups);

          if (groupIds.length === 1) {
            setSelectedGroup(groupIds[0]);
          }
        }
        setVerifyLoading(false);
      } catch {
        setVerifyError(true);
        setVerifyLoading(false);
      }
    }
    verifyFaculty();
  }, []);

  const currentPage = getCurrentPage();
  const { accesses } = useSelector((state) => state.auth);
  const accessModule = accesses.hierarchy.find(
    (el) => el.alias === "administrative"
  );

  function handleClearFilters() {
    if (isFaculty) {
      setSelectedGroup(facultyGroupIds.length === 1 ? facultyGroupIds[0] : "all");
      setSelectedTeacher(facultyStaffId);
    } else {
      setSelectedGroup("all");
      setSelectedTeacher("all");
    }
    setSelectedShift("all");
    setSelectedLevel("all");
    const range = getCurrentMonthRange();
    setPeriodFrom(range.from);
    setPeriodTo(range.to);
    setExpandedQuestionId(null);
  }

  function normalizeOptions(list, { idKey = "id", nameKey = "name" } = {}) {
    if (!Array.isArray(list)) return [];
    return list
      .map((item) => {
        if (item === null || item === undefined) return null;
        if (typeof item === "string" || typeof item === "number") {
          const value = String(item);
          return { id: value, name: value };
        }
        if (typeof item === "object") {
          const idValue =
            item[idKey] ?? item.value ?? item.code ?? item.key ?? item[nameKey];
          const nameValue =
            item[nameKey] ?? item.label ?? item.description ?? item[idKey];
          if (idValue === undefined || nameValue === undefined) return null;
          return { id: String(idValue), name: String(nameValue) };
        }
        return null;
      })
      .filter(Boolean);
  }

  async function loadData() {
    const params = {};
    if (periodFrom && periodTo) {
      params.period_from = periodFrom;
      params.period_to = periodTo;
    }
    if (isFaculty) {
      params.group_ids = selectedGroup === "all"
        ? facultyGroupIds.join(",")
        : selectedGroup;
    } else if (selectedGroup !== "all") {
      params.group = selectedGroup;
    }
    if (selectedTeacher !== "all") params.teacher = selectedTeacher;
    if (selectedShift !== "all") params.shift = selectedShift;
    if (selectedLevel !== "all") params.level = selectedLevel;
    if (viewMode === "text") {
      params.text_answer = "yes";
    }

    const { data } = await api.get("/surveys-answers", { params });

    if (viewMode === "text") {
      setTextAnswers(data.rows || []);
      setSurveys([]);
    } else if (data.rows.length > 0) {
      setSurveys(data.rows);
      setTextAnswers([]);
    } else {
      setSurveys([]);
      setTextAnswers([]);
    }
  }

  const handlePrint = async () => {
    if (filtersDisabled) return;
    setGeneratingPdf(true);
    try {
      const params = new URLSearchParams();
      params.set("period_from", periodFrom);
      params.set("period_to", periodTo);
      if (isFaculty) {
        params.set("group_ids", selectedGroup === "all"
          ? facultyGroupIds.join(",")
          : selectedGroup);
      } else if (selectedGroup !== "all") {
        params.set("group", selectedGroup);
      }
      if (selectedTeacher !== "all") params.set("teacher", selectedTeacher);
      if (selectedShift !== "all") params.set("shift", selectedShift);
      if (selectedLevel !== "all") params.set("level", selectedLevel);

      const response = await api.get(`/surveys-answers/pdf?${params.toString()}`, {
        responseType: "blob",
      });

      const filename =
        getFilenameFromContentDisposition(response.headers["content-disposition"]) ||
        `surveys_${Date.now()}.pdf`;
      const pdfBlob = new Blob([response.data], {
        type: response.headers["content-type"] || "application/pdf",
      });
      const opened = openBlobInNewTab(pdfBlob, {
        filename: filename.endsWith(".pdf") ? filename : `${filename}.pdf`,
      });
      if (!opened) {
        toast("Pop-up blocked. Allow pop-ups to preview the PDF.", {
          type: "error",
          autoClose: 3000,
        });
      }
    } catch (error) {
      console.error("Error generating PDF:", error);
    } finally {
      setGeneratingPdf(false);
    }
  };

  useEffect(() => {
    if (verifyLoading) return;
    loadData();
  }, [
    verifyLoading,
    periodFrom,
    periodTo,
    selectedGroup,
    selectedTeacher,
    selectedShift,
    selectedLevel,
    viewMode,
  ]);

  async function fetchAvailableFilters() {
    if (!periodFrom || !periodTo) return;
    setLoadingFilters(true);
    try {
      const availableParams = { period_from: periodFrom, period_to: periodTo };
      if (isFaculty) availableParams.group_ids = facultyGroupIds.join(",");
      const { data } = await api.get("/surveys-answers/available-filters", {
        params: availableParams,
      });

      const nextShifts = normalizeOptions(data.shifts);
      const nextLevels = normalizeOptions(data.levels);

      if (!isFaculty) {
        const nextGroups = normalizeOptions(data.groups);
        const nextTeachers = normalizeOptions(data.teachers);
        setGroups(nextGroups);
        setTeachers(nextTeachers);

        if (selectedGroup !== "all" && !nextGroups.some((g) => g.id === selectedGroup)) {
          setSelectedGroup("all");
        }
        if (selectedTeacher !== "all" && !nextTeachers.some((t) => t.id === selectedTeacher)) {
          setSelectedTeacher("all");
        }
      }

      setAvailableShifts(nextShifts);
      setAvailableLevels(nextLevels);

      if (selectedShift !== "all" && !nextShifts.some((s) => s.id === selectedShift)) {
        setSelectedShift("all");
      }
      if (selectedLevel !== "all" && !nextLevels.some((l) => l.id === selectedLevel)) {
        setSelectedLevel("all");
      }
    } catch (error) {
      console.error("Error fetching available filters:", error);
      if (!isFaculty) {
        setGroups([]);
        setTeachers([]);
      }
      setAvailableShifts([]);
      setAvailableLevels([]);
    } finally {
      setLoadingFilters(false);
    }
  }

  async function fetchFilters() {
    if (!periodFrom || !periodTo) return;
    setLoadingFilters(true);
    try {
      const params = {
        period_from: periodFrom,
        period_to: periodTo,
      };
      if (isFaculty) {
        params.group_ids = selectedGroup === "all"
          ? facultyGroupIds.join(",")
          : selectedGroup;
      } else if (selectedGroup !== "all") {
        params.group = selectedGroup;
      }
      if (selectedTeacher !== "all") params.teacher = selectedTeacher;
      if (selectedShift !== "all") params.shift = selectedShift;
      if (selectedLevel !== "all") params.level = selectedLevel;

      const { data } = await api.get("/surveys-answers/filters", { params });

      const nextShifts = normalizeOptions(data.shifts);
      const nextLevels = normalizeOptions(data.levels);

      if (!isFaculty) {
        const nextGroups = normalizeOptions(data.groups);
        const nextTeachers = normalizeOptions(data.teachers);
        setGroups(nextGroups);
        setTeachers(nextTeachers);

        if (selectedGroup !== "all" && !nextGroups.some((g) => g.id === selectedGroup)) {
          setSelectedGroup("all");
        }
        if (selectedTeacher !== "all" && !nextTeachers.some((t) => t.id === selectedTeacher)) {
          setSelectedTeacher("all");
        }
      }

      setAvailableShifts(nextShifts);
      setAvailableLevels(nextLevels);

      if (selectedShift !== "all" && !nextShifts.some((s) => s.id === selectedShift)) {
        setSelectedShift("all");
      }
      if (selectedLevel !== "all" && !nextLevels.some((l) => l.id === selectedLevel)) {
        setSelectedLevel("all");
      }
    } catch (error) {
      console.error("Error fetching filters:", error);
      if (!isFaculty) {
        setGroups([]);
        setTeachers([]);
      }
      setAvailableShifts([]);
      setAvailableLevels([]);
    } finally {
      setLoadingFilters(false);
    }
  }

  useEffect(() => {
    if (verifyLoading) return;
    fetchAvailableFilters();
  }, [verifyLoading, periodFrom, periodTo]);

  useEffect(() => {
    if (verifyLoading) return;
    fetchFilters();
  }, [verifyLoading, periodFrom, periodTo, selectedGroup, selectedTeacher, selectedShift, selectedLevel]);

  const textQuestions = useMemo(() => {
    if (!textAnswers.length) return [];
    const map = new Map();
    textAnswers.forEach((row) => {
      const key = row.question_id ?? row.question ?? row.id;
      if (!map.has(key)) {
        map.set(key, {
          question_id: row.question_id ?? row.id,
          question: row.question,
          answers: [],
        });
      }
      map.get(key).answers.push(row);
    });
    return Array.from(map.values()).map((item) => ({
      ...item,
      answers: item.answers.sort((a, b) => {
        if (!a.date || !b.date) return 0;
        return a.date > b.date ? -1 : 1;
      }),
    }));
  }, [textAnswers]);

  const selectedQuestion = useMemo(() => {
    return (
      textQuestions.find(
        (question) => question.question_id === expandedQuestionId
      ) ||
      textQuestions[0] ||
      null
    );
  }, [expandedQuestionId, textQuestions]);

  useEffect(() => {
    if (viewMode !== "text") return;
    if (expandedQuestionId !== null) return;
    if (!textQuestions.length) return;
    setExpandedQuestionId(textQuestions[0].question_id);
  }, [expandedQuestionId, textQuestions, viewMode]);

  const pageAccess = accessModule.children.find((el) => el.alias === "surveys");
  const hasAccessToAlias = (alias) => {
    return pageAccess.children.find((el) => el.alias === alias)
      ?.MenuHierarchyXGroup?.view;
  };

  if (verifyLoading) {
    return (
      <div className="h-full bg-white flex flex-1 items-center justify-center rounded-tr-2xl">
        <p className="text-sm text-gray-500">Loading...</p>
      </div>
    );
  }

  if (verifyError) {
    return (
      <div className="h-full bg-white flex flex-1 items-center justify-center rounded-tr-2xl">
        <p className="text-sm text-red-500">
          Unable to verify staff type. Please try again or contact support.
        </p>
      </div>
    );
  }

  const visibleGroups = isFaculty
    ? facultyGroups
    : groups;

  const groupSelectLocked = isFaculty && facultyGroupIds.length === 1;

  return (
    <div className="h-full bg-white flex flex-1 flex-col justify-start items-start rounded-tr-2xl px-4 overflow-y-scroll">
      <PageHeader>
        <Breadcrumbs currentPage={currentPage} />
        <FiltersBar>
          <button
            type="button"
            onClick={handlePrint}
            disabled={filtersDisabled || generatingPdf}
            className="flex items-center gap-2 text-xs text-gray-600 hover:text-gray-800 disabled:opacity-50"
          >
            <Printer size={14} />
            {generatingPdf ? "Generating..." : "Print"}
          </button>
        </FiltersBar>
      </PageHeader>

      <div className="px-4 py-3 border-b bg-gray-50 flex flex-wrap gap-3 items-end">
        <div className="flex flex-col gap-1">
          <label className="text-xs font-semibold text-gray-600">
            Period from / to
          </label>
          <div className="flex gap-2">
            <div className="flex flex-col gap-1">
              <div className="px-2 text-xs">From</div>
              <input
                type="date"
                value={periodFrom}
                onChange={(e) => setPeriodFrom(e.target.value)}
                className="w-32 border rounded-xs p-2 px-4 text-sm"
              />
            </div>
            <div className="flex flex-col gap-1">
              <div className="px-2 text-xs">To</div>
              <input
                type="date"
                value={periodTo}
                onChange={(e) => setPeriodTo(e.target.value)}
                className="w-32 border rounded-xs p-2 px-4 text-sm"
              />
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-xs font-semibold text-gray-600">Group</label>
          <select
            value={selectedGroup}
            onChange={(e) => setSelectedGroup(e.target.value)}
            disabled={loadingFilters || filtersDisabled || groupSelectLocked}
            className="px-2 py-1.5 text-xs border rounded-sm focus:ring-1 focus:ring-mila_orange w-48 disabled:opacity-50"
          >
            <option value="all">All Groups</option>
            {visibleGroups.map((group) => (
              <option key={group.id} value={group.id}>
                {group.name}
              </option>
            ))}
          </select>
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-xs font-semibold text-gray-600">Teacher</label>
          <select
            value={selectedTeacher}
            onChange={(e) => setSelectedTeacher(e.target.value)}
            disabled={loadingFilters || filtersDisabled || isFaculty}
            className="px-2 py-1.5 text-xs border rounded-sm focus:ring-1 focus:ring-mila_orange w-48 disabled:opacity-50"
          >
            {!isFaculty && <option value="all">All Teachers</option>}
            {teachers.map((teacher) => (
              <option key={teacher.id} value={teacher.id}>
                {teacher.name}
              </option>
            ))}
          </select>
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-xs font-semibold text-gray-600">Shift</label>
          <select
            value={selectedShift}
            onChange={(e) => setSelectedShift(e.target.value)}
            disabled={loadingFilters || filtersDisabled}
            className="px-2 py-1.5 text-xs border rounded-sm focus:ring-1 focus:ring-mila_orange w-48 disabled:opacity-50"
          >
            <option value="all">All Shifts</option>
            {availableShifts.map((shift) => (
              <option key={shift.id} value={shift.id}>
                {shift.name}
              </option>
            ))}
          </select>
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-xs font-semibold text-gray-600">Level</label>
          <select
            value={selectedLevel}
            onChange={(e) => setSelectedLevel(e.target.value)}
            disabled={loadingFilters || filtersDisabled}
            className="px-2 py-1.5 text-xs border rounded-sm focus:ring-1 focus:ring-mila_orange w-48 disabled:opacity-50"
          >
            <option value="all">All Levels</option>
            {availableLevels.map((level) => (
              <option key={level.id} value={level.id}>
                {level.name}
              </option>
            ))}
          </select>
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-xs font-semibold text-gray-600">View</label>
          <select
            value={viewMode}
            onChange={(e) => setViewMode(e.target.value)}
            disabled={filtersDisabled}
            className="px-2 py-1.5 text-xs border rounded-sm focus:ring-1 focus:ring-mila_orange w-48 disabled:opacity-50"
          >
            <option value="charts">Charts</option>
            <option value="text">Text Answers</option>
          </select>
        </div>
        <button
          type="button"
          onClick={handleClearFilters}
          disabled={loadingFilters}
          className="text-xs border rounded-sm px-3 py-1.5 text-gray-600 hover:text-gray-800 hover:border-gray-400 disabled:opacity-50"
        >
          Clear filters
        </button>
        <div className="flex-1" />
        <div className="text-xs text-gray-500">
          <p>
            {filtersDisabled
              ? "Select a period to enable filters"
              : "Filters update automatically"}
          </p>
        </div>
      </div>

      <div className="flex flex-1 w-full p-4">
        {viewMode === "text" ? (
          <div className="flex gap-4 w-full">
            <div className="w-80 flex-shrink-0 overflow-y-auto space-y-2 bg-gray-50 p-2 rounded-lg" style={{ maxHeight: 'calc(100vh - 300px)' }}>
                  {textQuestions.map((question) => {
                const isSelected =
                  expandedQuestionId === question.question_id;
                return (
                  <button
                    key={question.question_id}
                    onClick={() => setExpandedQuestionId(question.question_id)}
                    className={`w-full text-left border rounded-lg px-4 py-3 shadow-sm transition-colors ${
                      isSelected
                        ? "bg-mila_orange text-white border-mila_orange"
                        : "bg-white hover:bg-gray-50"
                    }`}
                  >
                    <div className="text-sm font-medium line-clamp-2">
                      {question.question}
                    </div>
                    <div className="text-xs mt-1 opacity-75">
                      {question.answers.length} answer(s)
                    </div>
                  </button>
                );
              })}
            </div>

            <div className="flex-1 flex flex-col bg-white border rounded-lg shadow-sm" style={{ maxHeight: 'calc(100vh - 300px)' }}>
              <div className="border-b px-6 py-4 bg-gray-50 flex-shrink-0">
                <h3 className="text-lg font-semibold text-gray-800">
                  {selectedQuestion?.question || "Select a question"}
                </h3>
                <p className="text-sm text-gray-600 mt-1">
                  {selectedQuestion
                    ? `${selectedQuestion.answers.length} answer(s)`
                    : "Click a question to see answers"}
                </p>
              </div>

              <div className="flex-1 overflow-y-auto p-6">
                {selectedQuestion ? (
                  <div className="space-y-4">
                    {selectedQuestion.answers.map((answer, idx) => (
                      <div
                        key={idx}
                        className="bg-gray-50 border border-gray-200 rounded-lg px-4 py-3"
                      >
                        <div className="text-sm text-gray-900 mb-2">
                          {answer.answer_description || answer.answer}
                        </div>
                        <div className="text-xs text-gray-500">
                          {answer.date}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="flex items-center justify-center h-full text-gray-400">
                    No question selected.
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div className="flex-1 w-full flex flex-col pt-6 overflow-y-auto">
            <div className="min-w-[1200px] w-full overflow-x-auto">
              <div className="grid grid-cols-2 gap-4">
                {surveys.map((survey, index) => {
                  if (survey.type === "Rating") {
                    return <RateChart key={index} survey={survey} />;
                  } else if (survey.type === "Pick Many") {
                    return <RateChart key={index} survey={survey} />;
                  } else if (survey.type === "Pick One") {
                    return <RateChart key={index} survey={survey} />;
                  } else if (survey.type === "Yes or No") {
                    return <RateChart key={index} survey={survey} />;
                  } else if (survey.type === "Text Area") {
                    return null;
                  }
                  return null;
                })}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
